import type { EntityId, EntityState, WorldPoint } from './types';
import { SpatialIndex } from './spatial-index';

export const SEPARATION_BUDGET = 20;

export interface SeparationProposal {
  entityId: EntityId;
  delta: WorldPoint;
}
const limitedDelta = (delta: WorldPoint): WorldPoint => {
  const length = Math.hypot(delta.x, delta.y);
  if (length <= SEPARATION_BUDGET || length === 0) return delta;
  return { x: delta.x / length * SEPARATION_BUDGET, y: delta.y / length * SEPARATION_BUDGET };
};

export function computeFriendlyGroundSeparation(entities: readonly EntityState[]): SeparationProposal[] {
  const moving = entities
    .filter((entity) => !entity.dead
      && !entity.removed
      && entity.deployRemaining <= 0
      && entity.movement === 'ground'
      && entity.kind === 'troop')
    .slice()
    .sort((left, right) => left.id - right.id);
  if (moving.length < 2) return [];

  const maximumRadius = Math.max(...moving.map((entity) => entity.radius));
  const index = new SpatialIndex(moving, Math.max(1, maximumRadius * 4));
  const accumulated = new Map<EntityId, { x: number; y: number }>();
  const add = (entityId: EntityId, x: number, y: number) => {
    const existing = accumulated.get(entityId) ?? { x: 0, y: 0 };
    existing.x += x;
    existing.y += y;
    accumulated.set(entityId, existing);
  };

  for (const left of moving) {
    const candidates = index.query(left.position, left.radius + index.maxRadius)
      .filter((right) => right.id > left.id && right.owner === left.owner)
      .sort((a, b) => a.id - b.id);
    for (const right of candidates) {
      const dx = right.position.x - left.position.x;
      const dy = right.position.y - left.position.y;
      const current = Math.hypot(dx, dy);
      const minimum = (left.radius + right.radius) * 0.65;
      if (current >= minimum) continue;
      const nx = current > 0 ? dx / current : left.id < right.id ? 1 : -1;
      const ny = current > 0 ? dy / current : 0;
      const push = Math.min(SEPARATION_BUDGET, (minimum - current) / 2);
      add(left.id, -nx * push, -ny * push);
      add(right.id, nx * push, ny * push);
    }
  }

  return [...accumulated.entries()]
    .sort(([left], [right]) => left - right)
    .map(([entityId, delta]) => ({ entityId, delta: limitedDelta(delta) }));
}
